import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faGripVertical, faXmark} from "@fortawesome/pro-regular-svg-icons";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";

import useProjectOrganisationContext from "@/pages/takeoff/context/project-organisation/use-project-organisation-context.ts";
import FileType from "@/models/enums/file-type.ts";
import FileData2D from "@/pages/takeoff/context/project-organisation/models/file-data-2d.ts";
import TruncatedText from "@/components/text/truncated-text";
import ProjectEditButton from "@/components/buttons/project-edit-button";

const planDragType = 'application/x-baamer-plan-id';
type ScopeType = 'project' | 'building' | 'storey';

interface PlanSource {
    item: FileData2D;
    scope: ScopeType;
    buildingId?: string;
    storeyId?: string;
}

export interface File2DListProps {
    itemsSource: FileData2D[];
    scope: ScopeType;
    buildingId?: string;
    storeyId?: string;
    noDataText?: string;
}

const File2DList: React.FC<File2DListProps> = ({itemsSource, scope, buildingId, storeyId, noDataText = 'Aucun fichier'}) => {
    const { canEdit: canEditProject } = useProjectEditAccess();

    const {data, addFile, removeFile, removePendingFile, getPlanFileData, setPlanFileData, removePlanFileData, isSaving} = useProjectOrganisationContext();
    const canEdit = canEditProject && !isSaving;

    const findSource = (fileId: string): PlanSource | undefined => {
        const projectFile = data?.files2d.find((file) => file.id === fileId);
        if (projectFile) {
            return {item: projectFile, scope: 'project'};
        }

        const sources = data?.buildings.flatMap<PlanSource>((building) => {
            const buildingFiles = building.files2d.map<PlanSource>((item) => ({
                item,
                scope: 'building',
                buildingId: building.id
            }));

            const storeyFiles = building.storeys.flatMap((storey) => {
                const files = storey.files2d.map<PlanSource>((item) => ({
                    item,
                    scope: 'storey',
                    buildingId: building.id,
                    storeyId: storey.id
                }));

                return files;
            });

            return [...buildingFiles, ...storeyFiles];
        });

        return sources?.find((source) => source.item.id === fileId);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        if (!event.dataTransfer.types.includes(planDragType)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (!canEdit) {
            return;
        }

        const source = findSource(event.dataTransfer.getData(planDragType));
        if (!source || itemsSource.some((file) => file.id === source.item.id)) {
            return;
        }

        const {item} = source;
        const planData = getPlanFileData(source.storeyId, item.id);

        removeFile(source.scope, FileType.File2D, item.id, source.buildingId, source.storeyId);
        addFile(scope, FileType.File2D, item.id, item.name, item.revision, item.relativePath, item.fileMetaObject, buildingId, storeyId);

        if (scope === 'storey' && storeyId && planData) {
            setPlanFileData(storeyId, item.id, planData);
        }

        if (source.storeyId) {
            removePlanFileData(source.storeyId, item.id);
        }
    };

    return (
        <div
            className={scope === 'project' ? 'h-full min-h-32' : 'min-h-8'}
            onDragEnter={(event) => {
                if (event.dataTransfer.types.includes(planDragType)) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            }}
            onDragOver={(event) => {
                if (event.dataTransfer.types.includes(planDragType)) {
                    event.preventDefault();
                    event.stopPropagation();
                    const {dataTransfer} = event;
                    dataTransfer.dropEffect = canEdit ? 'move' : 'none';
                }
            }}
            onDrop={handleDrop}>
            {itemsSource.length === 0 && noDataText && (
                <div className='py-1 text-sm text-gray-500'>{noDataText}</div>
            )}

            <ul className='m-0 list-none p-0 text-sm' aria-label='Plans PDF'>
                {itemsSource.map((item) => (
                    <li
                        key={item.id}
                        draggable={canEdit}
                        className={`flex min-w-0 flex-row items-center gap-x-2 border-b border-gray-200 px-2 py-1 last:border-b-0 ${canEdit ? 'cursor-grab active:cursor-grabbing' : ''}`}
                        onDragStart={(event) => {
                            event.stopPropagation();
                            const {dataTransfer} = event;
                            dataTransfer.setData(planDragType, item.id);
                            dataTransfer.effectAllowed = 'move';
                        }}>
                        <TruncatedText className='min-w-0 flex-1 select-none' text={item.name} />

                        {scope === 'project' && (
                            <ProjectEditButton
                                variant='ghost'
                                size='2xs'
                                disabled={isSaving}
                                draggable={false}
                                title={`Supprimer le plan ${item.name}`}
                                aria-label={`Supprimer le plan ${item.name}`}
                                onDragStart={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    removePendingFile(item.id);
                                }}>
                                <FontAwesomeIcon icon={faXmark} className='text-red-500' />
                            </ProjectEditButton>
                        )}

                        {canEdit && <FontAwesomeIcon icon={faGripVertical} className='text-gray-400' />}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default File2DList;
